<template>
  <v-container>
    <v-row>
      <v-col cols="12" class="d-flex align-center">
        <h1>Verification Snapshots</h1>
        <v-spacer />
        <v-btn color="primary" depressed @click="dialog = true">
          Take snapshot now
        </v-btn>
      </v-col>
    </v-row>

    <v-row v-if="!snapshots.length">
      <v-col cols="12">
        <p class="grey--text">No snapshots yet. The Cloud Scheduler usually takes one weekly.</p>
      </v-col>
    </v-row>

    <v-row v-for="snap in snapshots" :key="snap.id" class="mb-4">
      <v-col cols="12">
        <v-card outlined>
          <v-card-title>
            {{ formatDate(snap.period_start) }} → {{ formatDate(snap.period_end) }}
            <v-spacer />
            <v-chip small :color="snap.data_quality === 'full' ? 'success' : 'warning'">
              {{ snap.data_quality }}
            </v-chip>
          </v-card-title>
          <v-card-subtitle>
            taken {{ formatDateTime(snap.snapshot_taken_at) }}
            · triggered by {{ snap.triggered_by_name || 'scheduler' }}
          </v-card-subtitle>

          <v-card-text>
            <v-row>
              <v-col cols="6" md="3">
                <v-card outlined class="text-center">
                  <v-card-title class="headline justify-center">{{ snap.aggregates.tags_reviewed || 0 }}</v-card-title>
                  <v-card-subtitle>Tags reviewed</v-card-subtitle>
                </v-card>
              </v-col>
              <v-col cols="6" md="3">
                <v-card outlined class="text-center">
                  <v-card-title class="headline justify-center">{{ snap.aggregates.tags_incorrect || 0 }}</v-card-title>
                  <v-card-subtitle>Incorrect kept</v-card-subtitle>
                </v-card>
              </v-col>
              <v-col cols="6" md="3">
                <v-card outlined class="text-center">
                  <v-card-title class="headline justify-center">{{ snap.aggregates.tags_removed || 0 }}</v-card-title>
                  <v-card-subtitle>Removed</v-card-subtitle>
                </v-card>
              </v-col>
              <v-col cols="6" md="3">
                <v-card outlined class="text-center">
                  <v-card-title class="headline justify-center">{{ snap.aggregates.tags_added || 0 }}</v-card-title>
                  <v-card-subtitle>Added</v-card-subtitle>
                </v-card>
              </v-col>
            </v-row>

            <h3 class="mt-4">Per-tag breakdown</h3>
            <v-simple-table v-if="hasTagBreakdown(snap)">
              <template v-slot:default>
                <thead>
                  <tr>
                    <th>Tag</th>
                    <th>Incorrect kept</th>
                    <th>Removed</th>
                    <th>Added</th>
                  </tr>
                </thead>
                <tbody>
                  <tr v-for="row in tagRows(snap)" :key="row.tagId">
                    <td>{{ row.tagName }}</td>
                    <td>{{ row.incorrect_kept }}</td>
                    <td>{{ row.removed }}</td>
                    <td>{{ row.added }}</td>
                  </tr>
                </tbody>
              </template>
            </v-simple-table>
            <p v-else class="grey--text">No per-tag activity in this period.</p>

            <h3 class="mt-4">Tagger timing (seconds)</h3>
            <v-simple-table v-if="hasTimingRows(snap.tagger_timing)">
              <template v-slot:default>
                <thead><tr><th>User</th><th>Count</th><th>Avg</th><th>p50</th><th>p95</th></tr></thead>
                <tbody>
                  <tr v-for="row in timingRows(snap.tagger_timing)" :key="row.userId">
                    <td>{{ row.userName }}</td>
                    <td>{{ row.count }}</td>
                    <td>{{ format(row.avg_seconds) }}</td>
                    <td>{{ format(row.p50) }}</td>
                    <td>{{ format(row.p95) }}</td>
                  </tr>
                </tbody>
              </template>
            </v-simple-table>
            <p v-else class="grey--text">No tagger activity.</p>

            <h3 class="mt-4">QC timing (seconds)</h3>
            <v-simple-table v-if="hasTimingRows(snap.qc_timing)">
              <template v-slot:default>
                <thead><tr><th>User</th><th>Count</th><th>Avg</th><th>p50</th><th>p95</th></tr></thead>
                <tbody>
                  <tr v-for="row in timingRows(snap.qc_timing)" :key="row.userId">
                    <td>{{ row.userName }}</td>
                    <td>{{ row.count }}</td>
                    <td>{{ format(row.avg_seconds) }}</td>
                    <td>{{ format(row.p50) }}</td>
                    <td>{{ format(row.p95) }}</td>
                  </tr>
                </tbody>
              </template>
            </v-simple-table>
            <p v-else class="grey--text">No QC activity.</p>
          </v-card-text>
        </v-card>
      </v-col>
    </v-row>

    <v-dialog v-model="dialog" max-width="400">
      <v-card>
        <v-card-title>Take snapshot</v-card-title>
        <v-card-text>
          <v-text-field label="Period start" v-model="newSnap.period_start" type="date" outlined dense />
          <v-text-field label="Period end (exclusive)" v-model="newSnap.period_end" type="date" outlined dense />
        </v-card-text>
        <v-card-actions>
          <v-spacer />
          <v-btn text @click="dialog = false">Cancel</v-btn>
          <v-btn color="primary" depressed @click="doTakeSnapshot" :loading="taking">Take</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </v-container>
</template>

<script>
import { mapActions, mapState } from 'vuex'

export default {
  name: 'VerificationSnapshots',
  data () {
    return {
      dialog: false,
      taking: false,
      newSnap: { period_start: '', period_end: '' },
    }
  },
  computed: {
    ...mapState({
      snapshots: (s) => s.verification.snapshots,
      allTags: (s) => s.tags.tags,
      allUsers: (s) => s.users.users,
    }),
  },
  mounted () {
    this.fetchSnapshots()
    const now = new Date()
    const start = new Date(now); start.setDate(start.getDate() - 7)
    this.newSnap.period_end = now.toISOString().slice(0, 10)
    this.newSnap.period_start = start.toISOString().slice(0, 10)
  },
  methods: {
    ...mapActions(['fetchSnapshots', 'takeSnapshot']),
    async doTakeSnapshot () {
      this.taking = true
      try {
        await this.takeSnapshot({
          period_start: new Date(this.newSnap.period_start).toISOString(),
          period_end: new Date(this.newSnap.period_end).toISOString(),
        })
        this.dialog = false
      } catch (e) {
        alert('Snapshot failed: ' + (e.response && e.response.data ? JSON.stringify(e.response.data) : e.message))
      } finally {
        this.taking = false
      }
    },
    formatDate (iso) { return iso ? new Date(iso).toISOString().slice(0, 10) : '' },
    formatDateTime (iso) { return iso ? new Date(iso).toISOString().replace('T', ' ').slice(0, 19) : '' },
    format (n) { return n == null ? '—' : Number(n).toFixed(1) },
    hasTagBreakdown (snap) {
      return snap.per_tag_breakdown && Object.keys(snap.per_tag_breakdown).length > 0
    },
    tagRows (snap) {
      return Object.entries(snap.per_tag_breakdown).map(([tagId, counts]) => {
        const tag = this.allTags.find(t => String(t.id) === String(tagId))
        return {
          tagId,
          tagName: tag ? tag.name : `tag #${tagId}`,
          incorrect_kept: counts.incorrect_kept || 0,
          removed: counts.removed || 0,
          added: counts.added || 0,
        }
      })
    },
    hasTimingRows (timing) { return timing && Object.keys(timing).length > 0 },
    timingRows (timing) {
      return Object.entries(timing).map(([userId, stats]) => {
        const user = this.allUsers.find(u => String(u.id) === String(userId))
        return {
          userId,
          userName: user ? `${user.first_name} ${user.last_name}` : `user #${userId}`,
          count: stats.count,
          avg_seconds: stats.avg_seconds,
          p50: stats.p50,
          p95: stats.p95,
        }
      })
    },
  },
}
</script>
